import React from 'react';
import ReactDOM from 'react-dom';
import { useData, useField } from './EpcrDataContext';
import { Icons } from './EpcrIcons';

// Extract a NEMSIS element number from a path like "eResponse/eResponse.03".
// Returns null for non-standard paths (e.g. "eBilling/eBilling.ContactLast").
function elemFromPath(path) {
  if (!path) return null;
  const seg = path.split('/').pop();
  return /^e[A-Za-z]+\.\d+$/.test(seg) ? seg : null;
}

// Derive whether a field is required from its spec when not explicitly set.
// Mandatory/Required usage → show required indicator.
function isRequired(specOrNull, explicitRequired) {
  if (explicitRequired !== undefined) return Boolean(explicitRequired);
  if (!specOrNull) return false;
  return specOrNull.usage === "Mandatory" || specOrNull.usage === "Required";
}

// Build a validator function from spec restrictions. Returns null if no constraints.
// The returned function takes a string value and returns an error string or null.
function buildValidator(spec) {
  if (!spec) return null;
  const r = spec.restrictions || {};
  const checks = [];
  if (r.pattern) {
    let re;
    try { re = new RegExp('^(?:' + r.pattern + ')$'); } catch { /* invalid regex — skip */ }
    if (re) checks.push(v => re.test(String(v)) ? null : 'Invalid format');
  }
  if (r.minLength != null) {
    const min = parseInt(r.minLength, 10);
    checks.push(v => String(v).length >= min ? null : `Min ${min} characters`);
  }
  if (r.maxLength != null) {
    const max = parseInt(r.maxLength, 10);
    checks.push(v => String(v).length <= max ? null : `Max ${max} characters`);
  }
  if (!checks.length) return null;
  return (v) => {
    if (v == null || v === '') return null;
    for (const check of checks) { const err = check(v); if (err) return err; }
    return null;
  };
}

function FieldLabel({ children, nem, required }) {
  return (
    <label className="field-label">
      <span>{children}</span>
      {required && <span className="req">*</span>}
      {nem && <span className="nem">{nem}</span>}
    </label>
  );
}

// ─── Picker modal ──────────────────────────────────────────────────────────────
// Rendered via portal to document.body so it escapes all stacking contexts.

export function PickerModal({ elem, value, multi, onClose, onSelect, title }) {
  const data  = useData();
  const spec  = elem ? data.spec(elem) : null;
  const [q, setQ] = React.useState("");

  const options = data.options(elem);

  const grouped = React.useMemo(() => {
    const fq = q.trim().toLowerCase();
    const filtered = fq
      ? options.filter(o => (o.label || "").toLowerCase().includes(fq) || (o.value || "").toLowerCase().includes(fq))
      : options;
    const byCat = {};
    for (const o of filtered) {
      const cat = o.category || "";
      if (!byCat[cat]) byCat[cat] = [];
      byCat[cat].push(o);
    }
    return byCat;
  }, [options, q]);

  const specialValues = React.useMemo(() => {
    if (!spec) return [];
    const fq = q.trim().toLowerCase();
    const nv = (spec.nv || []).map(x => ({ value: x.code, label: x.label, kind: 'nv' }));
    const pn = (spec.pn || []).map(x => ({ value: x.code, label: x.label, kind: 'pn' }));
    const all = [...nv, ...pn];
    return fq ? all.filter(x => x.label.toLowerCase().includes(fq)) : all;
  }, [spec, q]);

  const isSelected = (v) => Array.isArray(value) ? value.includes(v) : value === v;
  const totalOptions = options.length + specialValues.length;

  return ReactDOM.createPortal(
    <div className="epcr-pm-back" onClick={onClose}>
      <div className="epcr-pm" onClick={e => e.stopPropagation()}>
        <div className="epcr-pm-head">
          <h3>{title}</h3>
          <span className="epcr-pm-elem">{elem}</span>
          <button className="epcr-pm-close" onClick={onClose}><Icons.close size={32} /></button>
        </div>
        <div className="epcr-pm-search">
          <input
            autoFocus
            placeholder={`Search ${totalOptions.toLocaleString()} options…`}
            value={q}
            onChange={e => setQ(e.target.value)}
          />
        </div>
        <div className="epcr-pm-list">
          {Object.keys(grouped).map(cat => (
            <React.Fragment key={cat || "_"}>
              {cat && <div className="epcr-pm-cat">{cat}</div>}
              {grouped[cat].slice(0, 200).map(o => (
                <button
                  key={o.value}
                  className={"epcr-pm-opt" + (isSelected(o.value) ? " sel" : "")}
                  onClick={() => onSelect(o.value)}
                >
                  <span className="epcr-pm-code">{o.value}</span>
                  {o.label}
                </button>
              ))}
            </React.Fragment>
          ))}

          {specialValues.length > 0 && (
            <>
              <div className="epcr-pm-cat epcr-pm-special-hd">Not Recorded / Pertinent Negative</div>
              {specialValues.map(o => (
                <button
                  key={o.value}
                  className={"epcr-pm-opt epcr-pm-special" + (o.kind === 'nv' ? " nv" : " pn") + (isSelected(o.value) ? " sel" : "")}
                  onClick={() => onSelect(o.value)}
                >
                  <span className="epcr-pm-code">{o.value}</span>
                  {o.label}
                </button>
              ))}
            </>
          )}

          {Object.keys(grouped).length === 0 && specialValues.length === 0 && (
            <div className="epcr-pm-empty">No matches</div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── TextField ─────────────────────────────────────────────────────────────────

export function TextField({ label, nem, path: pathProp, elem, placeholder, required, multiline, rows, suffix }) {
  const data      = useData();
  const path      = pathProp || (elem && data?.path(elem)) || null;
  const specElem  = elem || elemFromPath(path);
  const value     = useField(path) ?? "";
  const spec      = data.spec(specElem);
  const req       = isRequired(spec, required);
  const validator = React.useMemo(() => buildValidator(spec), [spec]);
  const [touched, setTouched] = React.useState(false);
  const error     = touched ? validator?.(value) ?? null : null;
  const onChange  = e => path && data.setReportPath(path, e.target.value);
  const cls       = "field-input" + (value ? "" : " empty") + (error ? " invalid" : "");
  const displayNem = nem || specElem;

  const inputEl = multiline
    ? <textarea className={cls} rows={rows || 4} value={value} onChange={onChange} onBlur={() => setTouched(true)} placeholder={placeholder || "—"} />
    : <input className={cls} value={value} onChange={onChange} onBlur={() => setTouched(true)} placeholder={placeholder || "—"} />;

  return (
    <div className="field" id={specElem ? `field-${specElem}` : undefined}>
      <FieldLabel nem={displayNem} required={req}>{label}</FieldLabel>
      {(suffix && !multiline)
        ? <div className="field-input-row">{inputEl}{suffix}</div>
        : inputEl
      }
      {error && <div className="field-error">{error}</div>}
    </div>
  );
}

// ─── NumericField ──────────────────────────────────────────────────────────────

export function NumericField({ label, nem, path: pathProp, elem, required, suffix }) {
  const data     = useData();
  const path     = pathProp || (elem && data?.path(elem)) || null;
  const specElem = elem || elemFromPath(path);
  const value    = useField(path) ?? "";
  const spec     = data.spec(specElem);
  const req      = isRequired(spec, required);
  const min      = spec?.restrictions?.minInclusive != null ? Number(spec.restrictions.minInclusive) : undefined;
  const max      = spec?.restrictions?.maxInclusive != null ? Number(spec.restrictions.maxInclusive) : undefined;
  const step     = spec?.baseType === 'decimal' ? 'any' : '1';
  const [touched, setTouched] = React.useState(false);
  const error = React.useMemo(() => {
    if (!touched || value === "" || value == null) return null;
    const n = Number(value);
    if (min !== undefined && n < min) return `Must be ≥ ${min}`;
    if (max !== undefined && n > max) return `Must be ≤ ${max}`;
    return null;
  }, [touched, value, min, max]);
  const onChange = e => {
    const v = e.target.value === "" ? "" : (spec?.baseType === 'decimal' ? parseFloat(e.target.value) : parseInt(e.target.value, 10));
    path && data.setReportPath(path, isNaN(v) ? "" : v);
  };
  const displayNem = nem || specElem;
  const inputEl = (
    <input
      type="number"
      className={"field-input" + (value === "" ? " empty" : "") + (error ? " invalid" : "")}
      value={value}
      onChange={onChange}
      onBlur={() => setTouched(true)}
      min={min}
      max={max}
      step={step}
      placeholder={min != null && max != null ? `${min}–${max}` : "—"}
    />
  );
  return (
    <div className="field" id={specElem ? `field-${specElem}` : undefined}>
      <FieldLabel nem={displayNem} required={req}>{label}</FieldLabel>
      {suffix
        ? <div className="field-input-row">{inputEl}{suffix}</div>
        : inputEl
      }
      {error && <div className="field-error">{error}</div>}
    </div>
  );
}

// ─── OptionField ───────────────────────────────────────────────────────────────

export function OptionField({ label, nem, path: pathProp, elem, inline = "auto", featured, shortcuts, required }) {
  const data    = useData();
  const path    = pathProp || (elem && data?.path(elem)) || null;
  const value   = useField(path);
  const spec    = data.spec(elem);
  const req     = isRequired(spec, required);
  const [open, setOpen] = React.useState(false);
  const options = data.options(elem) || [];
  const displayNem = nem || elem;

  // featured: specific value codes to show as inline buttons (rest via More)
  const featuredOpts = featured
    ? featured.map(code => options.find(o => o.value === code)).filter(Boolean)
    : null;

  const useInline = featuredOpts !== null
    ? true
    : (inline === true || (inline === "auto" && options.length <= 4 && options.length > 0));

  const handleShortcut = (sc) => {
    if (!path) return;
    if (sc.clear) data.setReportPath(path, "");
    else if (sc.value) data.setReportPath(path, value === sc.value ? "" : sc.value);
  };

  if (useInline) {
    const displayedOpts = featuredOpts || options;
    return (
      <div className="field" id={elem ? `field-${elem}` : undefined}>
        <FieldLabel nem={displayNem} required={req}>{label}</FieldLabel>
        <div className="opt-group">
          {displayedOpts.map(o => (
            <button
              key={o.value}
              className={"opt-btn" + (value === o.value ? " selected blue" : "")}
              onClick={() => path && data.setReportPath(path, value === o.value ? "" : o.value)}
            >
              {o.label}
            </button>
          ))}
          <button className="opt-btn menu" onClick={() => setOpen(true)}>
            More <Icons.menu size={14} />
          </button>
        </div>
        {open && (
          <PickerModal
            elem={elem} value={value} title={label}
            onClose={() => setOpen(false)}
            onSelect={v => { path && data.setReportPath(path, v); setOpen(false); }}
          />
        )}
      </div>
    );
  }

  const labelText = value ? data.label(elem, value) : "";
  const isSpecial = value && (
    (spec?.nv  || []).some(x => x.code  === String(value)) ||
    (spec?.pn  || []).some(x => x.code  === String(value))
  );

  return (
    <div className="field" id={elem ? `field-${elem}` : undefined}>
      <FieldLabel nem={displayNem} required={req}>{label}</FieldLabel>
      <div style={{ display: "flex", alignItems: "stretch", gap: 8 }}>
        <button
          className={"field-input" + (labelText ? (isSpecial ? " value-special" : "") : " empty")}
          style={{ textAlign: "left", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", flex: 1 }}
          onClick={() => setOpen(true)}
        >
          <span>{labelText || "Select…"}</span>
          <Icons.menu size={14} />
        </button>
        {shortcuts?.map((sc, i) => (
          <button
            key={i}
            className={"opt-btn" + (sc.value && value === sc.value ? " selected blue" : "")}
            onClick={() => handleShortcut(sc)}
          >
            {sc.label}
          </button>
        ))}
      </div>
      {open && (
        <PickerModal
          elem={elem} value={value} title={label}
          onClose={() => setOpen(false)}
          onSelect={v => { path && data.setReportPath(path, v); setOpen(false); }}
        />
      )}
    </div>
  );
}

// ─── MultiOptionField ──────────────────────────────────────────────────────────

export function MultiOptionField({ label, nem, path: pathProp, elem, required, shortcuts }) {
  const data  = useData();
  const path  = pathProp || (elem && data?.path(elem)) || null;
  const spec  = data.spec(elem);
  const req   = isRequired(spec, required);
  const raw   = useField(path);
  const value = Array.isArray(raw) ? raw : raw ? [raw] : [];
  const [open, setOpen] = React.useState(false);
  const displayNem = nem || elem;

  const toggle = v => {
    const next = value.includes(v) ? value.filter(x => x !== v) : [...value, v];
    path && data.setReportPath(path, next);
  };
  const remove = v => path && data.setReportPath(path, value.filter(x => x !== v));

  const handleShortcut = (sc) => {
    if (!path) return;
    if (sc.clear) data.setReportPath(path, []);
    else if (sc.value) toggle(sc.value);
  };

  const chipClass = (v) => {
    if ((spec?.nv || []).some(x => x.code === String(v))) return "chip nv";
    if ((spec?.pn || []).some(x => x.code === String(v))) return "chip pn";
    return "chip";
  };

  return (
    <div className="field" id={elem ? `field-${elem}` : undefined}>
      <FieldLabel nem={displayNem} required={req}>{label}</FieldLabel>
      <div className="select-wrap multi">
        {value.length === 0 && <span style={{ color: "var(--epcr-ink-4)", fontSize: 13, padding: "2px 6px" }}>—</span>}
        {value.map(v => (
          <span key={v} className={chipClass(v)}>
            {data.label(elem, v)}
            <button className="x" onClick={() => remove(v)} aria-label="remove"><Icons.close size={10} /></button>
          </span>
        ))}
        {shortcuts?.map((sc, i) => (
          <button
            key={i}
            className={"opt-btn" + (sc.value && value.includes(sc.value) ? " selected blue" : "")}
            onClick={() => handleShortcut(sc)}
          >
            {sc.label}
          </button>
        ))}
        <button className="chip-add" onClick={() => setOpen(true)} aria-label="open picker">
          <Icons.menu size={10} />
        </button>
      </div>
      {open && (
        <PickerModal elem={elem} value={value} multi title={label}
          onClose={() => setOpen(false)}
          onSelect={toggle}
        />
      )}
    </div>
  );
}

// ─── DateTimeField ─────────────────────────────────────────────────────────────

export function DateTimeField({ label, nem, path: pathProp, elem, required, suffix }) {
  const data     = useData();
  const path     = pathProp || (elem && data?.path(elem)) || null;
  const specElem = elem || elemFromPath(path);
  const spec     = data.spec(specElem);
  const req      = isRequired(spec, required);
  const value    = useField(path) ?? "";
  const display  = value ? value.substring(0, 16) : "";
  const onChange = e => {
    const v = e.target.value ? e.target.value + ":00-04:00" : "";
    path && data.setReportPath(path, v);
  };
  const displayNem = nem || specElem;
  const inputEl = <input type="datetime-local" className={"field-input" + (display ? "" : " empty")} value={display} onChange={onChange} />;
  return (
    <div className="field" id={specElem ? `field-${specElem}` : undefined}>
      <FieldLabel nem={displayNem} required={req}>{label}</FieldLabel>
      {suffix
        ? <div className="field-input-row">{inputEl}{suffix}</div>
        : inputEl
      }
    </div>
  );
}

// ─── AutoField ────────────────────────────────────────────────────────────────
// Schema-driven field router. Reads spec.maxOccurs and data.options() to pick
// the correct component — no hardcoded element lists needed.
// Accepts all props valid for any of the underlying field components.

export function AutoField({ label, nem, elem, path: pathProp, required, ...rest }) {
  const data = useData();
  const spec = elem ? data.spec(elem) : null;
  if (!spec) return null;

  const bt   = spec.baseType;
  const opts = data.options(elem);
  const props = { label, nem, elem, path: pathProp, required, ...rest };

  if (bt === 'dateTime' || bt === 'date') return <DateTimeField {...props} />;
  if (bt === 'base64Binary')              return null;

  if (opts.length > 0) {
    return spec.maxOccurs === 'M'
      ? <MultiOptionField {...props} />
      : <OptionField      {...props} />;
  }

  if (bt === 'integer' || bt === 'decimal' || bt === 'positiveInteger') return <NumericField {...props} />;
  return <TextField {...props} />;
}

// ─── StaticField ───────────────────────────────────────────────────────────────

export function StaticField({ label, nem, value, mono }) {
  return (
    <div className="field">
      <FieldLabel nem={nem}>{label}</FieldLabel>
      <div className="field-input" style={{
        background: "var(--epcr-bg-1)",
        fontFamily: mono ? "'IBM Plex Mono', monospace" : "inherit",
        color: value ? "var(--epcr-ink)" : "var(--epcr-ink-4)",
      }}>
        {value || "—"}
      </div>
    </div>
  );
}

// ─── Section ───────────────────────────────────────────────────────────────────

export function Section({ id, num, title, meta, children }) {
  return (
    <section id={id} className="section" data-section={id}>
      <div className="section-bar" style={{ backgroundColor: "rgb(238, 238, 238)" }}>
        <span className="num">{num}</span>
        <h2>{title}</h2>
        {meta && <span className="meta">{meta}</span>}
      </div>
      <div className="section-body">
        {children}
      </div>
    </section>
  );
}

// ─── SectionNav ────────────────────────────────────────────────────────────────

export function SectionNav({ sections, activeId, onClick }) {
  return (
    <nav className="snav">
      <div className="snav-header">Sections</div>
      {sections.map(s => (
        <button
          key={s.id}
          className={"snav-item" + (activeId === s.id ? " active" : "")}
          onClick={() => onClick(s.id)}
        >
          {s.label}
        </button>
      ))}
    </nav>
  );
}

function useActiveSection(scrollRef, sectionIds) {
  const [active, setActive] = React.useState(sectionIds[0]);
  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      let best = sectionIds[0];
      let bestY = -Infinity;
      for (const id of sectionIds) {
        const node = el.querySelector(`[data-section="${id}"]`);
        if (!node) continue;
        const top = node.getBoundingClientRect().top - el.getBoundingClientRect().top;
        if (top <= 120 && top > bestY) { best = id; bestY = top; }
      }
      setActive(best);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => el.removeEventListener("scroll", onScroll);
  }, [sectionIds.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps
  return active;
}

// ─── ViewLayout ────────────────────────────────────────────────────────────────

export function ViewLayout({ sections, children, subbar, addBar }) {
  const ids = sections.map(s => s.id);
  const scrollRef = React.useRef(null);
  const active = useActiveSection(scrollRef, ids);
  const onNavClick = id => {
    const el = scrollRef.current?.querySelector(`[data-section="${id}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <div className="main">
      <SectionNav sections={sections} activeId={active} onClick={onNavClick} />
      <div className="view-col">
        <div className="view" ref={scrollRef}>
          {subbar}
          {children}
        </div>
        {addBar}
      </div>
    </div>
  );
}
